/**
 * <ChampionStage> — un champion seul à l'écran, puis qui « se range » dans l'en-tête (molecule).
 *
 * Le geste de « Sam se range » (Wajih, 20/10), nommé une fois pour l'attente de la correction et pour
 * le quiz de prérequis. Deux places pour un même champion, et un seul visage de l'une à l'autre, sa
 * tête V16 (`<ChampionAvatar>`) :
 *  - **sur scène** (`docked` faux) : en grand, au milieu, ce qu'il dit sous lui (`stage`) — « Sam
 *    corrige ta copie », « Ta correction est prête ! », l'intro du quiz ;
 *  - **rangé** (`docked`) : en petit dans l'en-tête, ses mots à côté (`dock`), et la place libre
 *    dessous pour ce qu'il propose (`children` : le Défi éclair, une pause, la question).
 *
 * Passer de l'une à l'autre, c'est une chose à la fois : les mots de la scène sortent (`micro`) ; Sam
 * glisse et rapetisse jusqu'à sa place d'en-tête (`standard`), ses mots d'en-tête avec lui ; ENSUITE
 * seulement, ce qu'il propose entre, fondu et 12 px (`standard`). Le retour sur scène refait le chemin à
 * l'envers. Rien n'est remonté : ce qui est dessous (un défi en cours) garde son état.
 *
 * Sam est dessiné une fois, à sa taille de scène, et l'échelle le rapetisse : il ne change ni de
 * dessin ni d'image en route. Ses deux places se déduisent des TAILLES mesurées (`onLayout`) de la
 * scène, de sa colonne et de la ligne d'en-tête, jamais de la position d'une place : sur le web,
 * `onLayout` ne se redit qu'à un changement de taille (`ResizeObserver`), et une place qui bouge sans
 * changer de taille (la fenêtre élargie, le volet du live qui se pose, la barre d'adresse du téléphone
 * qui se replie) laissait Sam là où la première mesure l'avait mis, hors du centre. Tant que ces
 * tailles ne sont pas connues, il n'est pas peint (une image), pour ne jamais sauter.
 *
 * `arriving` : monté déjà rangé, il part quand même de sa place de scène. Pour une page dont l'étape
 * rangée suit toujours une étape sur scène qu'une autre instance montrait (le quiz de prérequis : sa
 * conversation se monte neuve au premier « Commencer le quiz »).
 *
 * En mouvement réduit, tout est posé aussitôt, à sa place.
 *
 * La place qui n'est pas montrée est transparente, ne se touche pas et n'est pas lue ; une fois son
 * fondu fini, elle sort aussi de la tabulation sur le web (`visibility`, comme le volet retiré du live) :
 * sans cela, Tab menait aux réponses du Défi éclair, invisibles sous « Ta correction est prête ! ».
 *
 * Composes DS pieces only — `<ChampionAvatar>`, `<Row>`.
 */
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Animated, Easing, Platform, StyleSheet as RNStyleSheet, View, type ViewStyle } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { STEP_RISE } from "../../atoms/StepStage"
import { useReduceMotion } from "../../hooks/useReduceMotion"

/** La taille de Sam sur scène, celle de l'ancien Sam en pied (110 à 120 px de large). */
export const CHAMPION_STAGE_SIZE = 120
/** Sa taille rangé : celle d'une ligne de conversation. */
const CHAMPION_DOCK_SIZE = 44

export type ChampionStageProps = {
    name: ChampionName
    /** Rangé dans l'en-tête (`true`), ou seul sur scène. */
    docked: boolean
    /** Sur scène : ce qu'il dit sous lui. */
    stage?: React.ReactNode
    /** Rangé : ses mots, à côté de lui. */
    dock?: React.ReactNode
    /** Rangé : ce qu'il propose, sous l'en-tête. Monté dès le départ, montré une fois Sam rangé. */
    children?: React.ReactNode
    /** Monté rangé, il part de sa place de scène. Défaut `false`. */
    arriving?: boolean
    testID?: string
}

/** Ce qui entre monte de 12 px en apparaissant. */
const risen = (value: Animated.Value) => [
    { translateY: value.interpolate({ inputRange: [0, 1], outputRange: [STEP_RISE, 0] }) },
]
/** Une place vide n'est pas lue par un lecteur d'écran. */
const hidden = { importantForAccessibility: "no-hide-descendants" as const, accessibilityElementsHidden: true }

export function ChampionStage(props: ChampionStageProps) {
    const { name, docked, stage, dock, children, arriving = false, testID } = props
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme

    // Où Sam est (ou va) : sur scène au montage, sauf monté rangé sans venir de la scène.
    const settled = useRef(docked && !arriving)
    const start = settled.current ? 1 : 0
    const sam = useRef(new Animated.Value(start)).current
    const dockWords = useRef(new Animated.Value(start)).current
    const body = useRef(new Animated.Value(start)).current
    const stageWords = useRef(new Animated.Value(1 - start)).current

    // La place où Sam est arrivé, son enchaînement fini : l'autre ne s'atteint plus au clavier.
    const [restsDocked, setRestsDocked] = useState(settled.current)

    // Les tailles d'où se déduisent les deux places : la scène entière, sa colonne, la ligne d'en-tête.
    const [width, setWidth] = useState<number>()
    const [height, setHeight] = useState<number>()
    const [stageColumnHeight, setStageColumnHeight] = useState<number>()
    const [dockRowHeight, setDockRowHeight] = useState<number>()
    const measured = width != null && height != null && stageColumnHeight != null && dockRowHeight != null

    // L'enchaînement en cours : arrêté seulement par le suivant, ou au démontage.
    const running = useRef<Animated.CompositeAnimation | null>(null)
    useEffect(() => () => running.current?.stop(), [])

    const { micro, standard } = motion
    const [x1, y1, x2, y2] = motion.easing
    useEffect(() => {
        if (!measured) return
        const target = docked ? 1 : 0
        if (reduceMotion) {
            running.current?.stop()
            settled.current = docked
            sam.setValue(target)
            dockWords.setValue(target)
            body.setValue(target)
            stageWords.setValue(1 - target)
            setRestsDocked(docked)
            return
        }
        if (settled.current === docked) {
            setRestsDocked(docked)
            return
        }
        settled.current = docked
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        const useNativeDriver = Platform.OS !== "web"
        const easing = Easing.bezier(x1, y1, x2, y2)
        const to = (value: Animated.Value, toValue: number, duration: number) =>
            Animated.timing(value, { toValue, duration, easing, useNativeDriver })
        const move = docked
            ? Animated.sequence([
                  to(stageWords, 0, micro),
                  Animated.parallel([to(sam, 1, standard), to(dockWords, 1, standard)]),
                  to(body, 1, standard),
              ])
            : Animated.sequence([
                  Animated.parallel([to(body, 0, micro), to(dockWords, 0, micro)]),
                  to(sam, 0, standard),
                  to(stageWords, 1, standard),
              ])
        running.current?.stop()
        running.current = move
        move.start(({ finished }) => {
            if (finished) setRestsDocked(docked)
        })
    }, [docked, measured, reduceMotion, sam, dockWords, body, stageWords, micro, standard, x1, y1, x2, y2])

    // Sam est posé au coin haut gauche, à sa taille de scène ; sa transformation le mène à sa place. Les
    // places suivent les styles plus bas, qui n'ont ni marge ni retrait (`root`, `dockRow`, `stageLayer`,
    // `stageColumn`) : sur scène, la première place de `stageColumn` (au milieu de la largeur,
    // `alignItems: center`), colonne centrée dans la hauteur de `stageLayer` (`absoluteFill`,
    // `justifyContent: center`) ; rangé, la première place de `dockRow`, collée à gauche, centrée dans sa
    // hauteur (`alignItems: center`). Les mêmes règles de flex sur le web et le natif ; toucher ces styles,
    // c'est revoir ce calcul (`ChampionStage.place.test.tsx`).
    const half = CHAMPION_STAGE_SIZE / 2
    const stageX = ((width ?? 0) - CHAMPION_STAGE_SIZE) / 2
    const stageY = ((height ?? 0) - (stageColumnHeight ?? 0)) / 2
    const dockX = CHAMPION_DOCK_SIZE / 2 - half
    const dockY = (dockRowHeight ?? 0) / 2 - half
    const samTransform = useMemo(
        (): Animated.WithAnimatedValue<ViewStyle>["transform"] => [
            { translateX: sam.interpolate({ inputRange: [0, 1], outputRange: [stageX, dockX] }) },
            { translateY: sam.interpolate({ inputRange: [0, 1], outputRange: [stageY, dockY] }) },
            {
                scale: sam.interpolate({
                    inputRange: [0, 1],
                    outputRange: [1, CHAMPION_DOCK_SIZE / CHAMPION_STAGE_SIZE],
                }),
            },
        ],
        [sam, stageX, stageY, dockX, dockY]
    )
    // Hors d'atteinte : la place qui n'est pas montrée, une fois son fondu fini.
    const dockGone = !docked && !restsDocked
    const stageGone = docked && restsDocked
    const bodyRise = useMemo(() => risen(body), [body])
    const stageRise = useMemo(() => risen(stageWords), [stageWords])

    return (
        <View
            style={styles.root}
            testID={testID}
            onLayout={(event) => {
                setWidth(event.nativeEvent.layout.width)
                setHeight(event.nativeEvent.layout.height)
            }}
        >
            <View
                style={[styles.dockRow, dockGone && styles.unreachable]}
                onLayout={(event) => setDockRowHeight(event.nativeEvent.layout.height)}
                {...(docked ? {} : hidden)}
            >
                <View style={styles.dockSlot} />
                <Animated.View style={[styles.grow, { opacity: dockWords }]}>{dock}</Animated.View>
            </View>
            <Animated.View
                style={[styles.body, dockGone && styles.unreachable, { opacity: body, transform: bodyRise }]}
                pointerEvents={docked ? "box-none" : "none"}
                {...(docked ? {} : hidden)}
            >
                {children}
            </Animated.View>
            <Animated.View
                style={[
                    styles.stageLayer,
                    stageGone && styles.unreachable,
                    { opacity: stageWords, transform: stageRise },
                ]}
                pointerEvents={docked ? "none" : "box-none"}
                {...(docked ? hidden : {})}
            >
                <View
                    style={styles.stageColumn}
                    onLayout={(event) => setStageColumnHeight(event.nativeEvent.layout.height)}
                >
                    <View style={styles.stageSlot} />
                    {stage}
                </View>
            </Animated.View>
            <Animated.View style={[styles.sam(measured), { transform: samTransform }]} pointerEvents="none">
                <ChampionAvatar name={name} size={CHAMPION_STAGE_SIZE} />
            </Animated.View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0 },
    dockRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["3"],
    },
    dockSlot: { width: CHAMPION_DOCK_SIZE, height: CHAMPION_DOCK_SIZE },
    grow: { flex: 1, minWidth: 0 },
    body: { flex: 1, minHeight: 0, marginTop: theme.spacing["5"] },
    stageLayer: {
        ...RNStyleSheet.absoluteFillObject,
        justifyContent: "center",
    },
    stageColumn: {
        alignItems: "center",
        gap: theme.spacing["4"],
    },
    stageSlot: { width: CHAMPION_STAGE_SIZE, height: CHAMPION_STAGE_SIZE },
    /** La place qui n'est pas montrée, son fondu fini : hors de la tabulation sur le web. */
    unreachable: { _web: { visibility: "hidden" } },
    /** Sam, posé au coin et déplacé par sa transformation ; pas peint avant d'être mesuré. */
    sam: (measured: boolean) => ({
        position: "absolute" as const,
        left: 0,
        top: 0,
        opacity: measured ? 1 : 0,
    }),
}))
